import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Eye, Heart, Music, Calendar, Globe, Tag } from 'lucide-react';
import { BreadcrumbNav } from '../../components/layout/BreadcrumbNav.js';
import { SongCard } from '../../components/song/SongCard.js';
import { songsService } from '../../services/songs.service.js';
import { lyricsService } from '../../services/lyrics.service.js';
import { formatNumber } from '../../lib/format.js';

function SongSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-4 w-64 bg-surface-hover rounded mb-6" />
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-4">
          <div className="h-8 w-72 bg-surface-hover rounded" />
          <div className="h-4 w-48 bg-surface-hover rounded" />
          <div className="space-y-2 mt-8">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="h-4 bg-surface-hover rounded"
                style={{ width: `${50 + Math.random() * 50}%` }}
              />
            ))}
          </div>
        </div>
        <div className="w-full lg:w-80 space-y-4">
          <div className="aspect-square bg-surface-hover rounded-xl" />
          <div className="h-4 w-32 bg-surface-hover rounded" />
          <div className="h-4 w-24 bg-surface-hover rounded" />
        </div>
      </div>
    </div>
  );
}

export default function SongPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: songData, isLoading: songLoading } = useQuery({
    queryKey: ['song', slug],
    queryFn: () => songsService.getSongBySlug(slug!),
    enabled: !!slug,
  });

  const song = songData?.data;

  const { data: lyricsData, isLoading: lyricsLoading } = useQuery({
    queryKey: ['lyrics', song?.id],
    queryFn: () => lyricsService.getLyrics(song!.id),
    enabled: !!song?.id,
  });

  const { data: relatedData } = useQuery({
    queryKey: ['songs', 'related', song?.id],
    queryFn: () => songsService.getRelated(song!.id),
    enabled: !!song?.id,
  });

  const viewMutation = useMutation({
    mutationFn: (songId: string) => songsService.registerView(songId),
  });

  // Register view on mount
  useEffect(() => {
    if (song?.id) {
      viewMutation.mutate(song.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song?.id]);

  const lyrics = lyricsData?.data;
  const relatedSongs = relatedData?.data ?? [];
  const lyricsLines = lyrics?.plainText?.split('\n') ?? [];

  if (songLoading) {
    return <SongSkeleton />;
  }

  if (!song) {
    return (
      <div className="text-center py-16">
        <Music size={64} className="mx-auto mb-4 text-text-muted opacity-50" />
        <h1 className="text-2xl font-bold text-text-primary mb-2">Cancion no encontrada</h1>
        <p className="text-text-secondary">La cancion que buscas no existe o fue eliminada.</p>
        <Link to="/" className="text-primary hover:text-primary/80 mt-4 inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    ...(song.genre ? [{ label: song.genre.name, to: `/genres/${song.genre.slug}` }] : []),
    { label: song.artist.name, to: `/artists/${song.artist.slug}` },
    { label: song.title },
  ];

  return (
    <>
      <Helmet>
        <title>
          {song.title} - {song.artist.name} | LyricsHub
        </title>
        <meta
          name="description"
          content={`Letra de ${song.title} de ${song.artist.name}. Encuentra letras de canciones en LyricsHub.`}
        />
        <meta property="og:title" content={`${song.title} - ${song.artist.name}`} />
        <meta property="og:description" content={`Letra de ${song.title} de ${song.artist.name}`} />
        {song.album?.coverUrl && <meta property="og:image" content={song.album.coverUrl} />}
      </Helmet>

      <BreadcrumbNav items={breadcrumbs} />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left Column - Lyrics */}
        <div className="flex-1 min-w-0">
          {/* Song Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-1">{song.title}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
              <Link
                to={`/artists/${song.artist.slug}`}
                className="font-medium hover:text-primary transition-colors"
              >
                {song.artist.name}
              </Link>
              {song.album && (
                <>
                  <span className="text-text-muted">·</span>
                  <Link
                    to={`/albums/${song.album.slug}`}
                    className="hover:text-primary transition-colors"
                  >
                    {song.album.title}
                  </Link>
                </>
              )}
              {song.releaseYear && (
                <>
                  <span className="text-text-muted">·</span>
                  <span>{song.releaseYear}</span>
                </>
              )}
            </div>

            {/* Tags and counters */}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              {song.genre && (
                <Link
                  to={`/genres/${song.genre.slug}`}
                  className="px-2.5 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors"
                >
                  {song.genre.name}
                </Link>
              )}
              {song.language && (
                <span className="px-2.5 py-0.5 text-xs rounded-full bg-surface-hover text-text-secondary font-medium">
                  {song.language.name}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-text-muted">
                <Eye size={13} />
                {formatNumber(song.viewCount ?? 0)} vistas
              </span>
              <span className="flex items-center gap-1 text-xs text-text-muted">
                <Heart size={13} />
                {formatNumber(song.likeCount ?? 0)} likes
              </span>
            </div>
          </div>

          {/* Lyrics */}
          <div className="bg-surface rounded-xl p-6 border border-border">
            {lyricsLoading ? (
              <div className="space-y-3 animate-pulse">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-surface-hover rounded"
                    style={{ width: `${40 + Math.random() * 50}%` }}
                  />
                ))}
              </div>
            ) : lyricsLines.length > 0 ? (
              <div className="space-y-0">
                {lyricsLines.map((line: any, i: number) => (
                  <div key={i} className="flex gap-3 py-0.5 group">
                    <span className="w-8 text-right text-xs text-text-muted/50 select-none shrink-0 pt-0.5 font-mono">
                      {i + 1}
                    </span>
                    <p
                      className={`text-text-primary leading-relaxed ${line.trim() === '' ? 'h-4' : ''}`}
                    >
                      {line}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-muted text-center py-8">Letra no disponible todavia.</p>
            )}
          </div>
        </div>

        {/* Right Column - Info Panel */}
        <aside className="w-full lg:w-80 shrink-0 lg:sticky lg:top-20 lg:self-start space-y-6">
          {/* Cover Image */}
          {song.album?.coverUrl ? (
            <img
              src={song.album.coverUrl}
              alt={song.title}
              className="w-full aspect-square object-cover rounded-xl"
            />
          ) : (
            <div className="w-full aspect-square rounded-xl bg-surface-hover flex items-center justify-center">
              <Music size={64} className="text-text-muted" />
            </div>
          )}

          {/* Song Info */}
          <div className="bg-surface rounded-xl p-4 border border-border space-y-3">
            <h3 className="font-semibold text-text-primary text-sm">Informacion</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-text-secondary">
                <Music size={14} className="text-text-muted shrink-0" />
                <span>Artista:</span>
                <Link
                  to={`/artists/${song.artist.slug}`}
                  className="text-primary hover:text-primary/80 truncate"
                >
                  {song.artist.name}
                </Link>
              </div>
              {song.album && (
                <div className="flex items-center gap-2 text-text-secondary">
                  <Music size={14} className="text-text-muted shrink-0" />
                  <span>Album:</span>
                  <Link
                    to={`/albums/${song.album.slug}`}
                    className="text-primary hover:text-primary/80 truncate"
                  >
                    {song.album.title}
                  </Link>
                </div>
              )}
              {song.releaseYear && (
                <div className="flex items-center gap-2 text-text-secondary">
                  <Calendar size={14} className="text-text-muted shrink-0" />
                  <span>Ano: {song.releaseYear}</span>
                </div>
              )}
              {song.genre && (
                <div className="flex items-center gap-2 text-text-secondary">
                  <Tag size={14} className="text-text-muted shrink-0" />
                  <span>Genero:</span>
                  <Link
                    to={`/genres/${song.genre.slug}`}
                    className="text-primary hover:text-primary/80"
                  >
                    {song.genre.name}
                  </Link>
                </div>
              )}
              {song.language && (
                <div className="flex items-center gap-2 text-text-secondary">
                  <Globe size={14} className="text-text-muted shrink-0" />
                  <span>Idioma: {song.language.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Related Songs */}
          {relatedSongs.length > 0 && (
            <div className="bg-surface rounded-xl p-4 border border-border">
              <h3 className="font-semibold text-text-primary text-sm mb-3">
                Mas de {song.artist.name}
              </h3>
              <div className="space-y-1">
                {relatedSongs.slice(0, 5).map((related: any) => (
                  <SongCard
                    key={related.id}
                    slug={related.slug}
                    title={related.title}
                    artistName={related.artistName}
                    artistSlug={related.artistSlug}
                    coverUrl={related.coverUrl}
                    viewCount={related.viewCount}
                    variant="list"
                  />
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </>
  );
}
